import { useEffect, useState } from "react";

interface TextScrambleProps {
  text: string;
  speed?: number;
  revealRate?: number;
  characterPool?: string;
  className?: string;
}

export const TextScramble = ({
  text,
  speed = 40,
  revealRate = 0.05,
  characterPool = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789",
  className = "",
}: TextScrambleProps) => {
  const [displayText, setDisplayText] = useState("");
  const [revealedIndices, setRevealedIndices] = useState<boolean[]>([]);
  const [spaceIndices, setSpaceIndices] = useState<number[]>([]);

  // Initialize with random characters and all positions unrevealed
  // Also identify space positions to preserve them
  useEffect(() => {
    // Find all space indices in the original text
    const spaces: number[] = [];
    for (let i = 0; i < text.length; i++) {
      if (text[i] === " ") {
        spaces.push(i);
      }
    }
    setSpaceIndices(spaces);

    // Generate initial random text, preserving spaces
    let initialRandomText = "";
    for (let i = 0; i < text.length; i++) {
      if (text[i] === " ") {
        initialRandomText += " "; // Preserve spaces
      } else {
        initialRandomText += characterPool.charAt(
          Math.floor(Math.random() * characterPool.length)
        );
      }
    }

    setDisplayText(initialRandomText);

    // Initialize revealed indices, marking spaces as already revealed
    const initialRevealed = Array(text.length).fill(false);
    spaces.forEach((index) => {
      initialRevealed[index] = true; // Mark spaces as already revealed
    });
    setRevealedIndices(initialRevealed);
  }, [text, characterPool]);

  // Animation effect
  useEffect(() => {
    if (revealedIndices.every((revealed) => revealed)) {
      // All characters revealed, ensure final text is exactly the target text
      setDisplayText(text);
      return; // Stop animation
    }

    const intervalId = setInterval(() => {
      // Update display text with new random characters for unrevealed positions
      setDisplayText((prevText) => {
        let newText = "";
        for (let i = 0; i < prevText.length; i++) {
          if (revealedIndices[i] || spaceIndices.includes(i)) {
            newText += text[i]; // Keep revealed characters and spaces
          } else {
            newText += characterPool.charAt(
              Math.floor(Math.random() * characterPool.length)
            );
          }
        }
        return newText;
      });

      // Randomly reveal new characters (excluding spaces which are already revealed)
      setRevealedIndices((prevRevealed) => {
        const newRevealed = [...prevRevealed];
        const unrevealed = newRevealed
          .map((revealed, index) =>
            revealed || spaceIndices.includes(index) ? -1 : index
          )
          .filter((idx) => idx !== -1);

        if (unrevealed.length > 0) {
          // Calculate how many characters to reveal this iteration
          const revealCount = Math.max(
            1,
            Math.floor(unrevealed.length * revealRate)
          );

          // Ensure at least one character is revealed each time
          const actualRevealCount = Math.min(unrevealed.length, revealCount);

          // Randomly select indices to reveal
          for (let i = 0; i < actualRevealCount; i++) {
            if (unrevealed.length === 0) break;
            const randomIndex = Math.floor(Math.random() * unrevealed.length);
            const indexToReveal = unrevealed[randomIndex];
            newRevealed[indexToReveal] = true;
            unrevealed.splice(randomIndex, 1);
          }
        }

        // If we're close to the end (few characters left), increase the chance of completion
        if (newRevealed.filter((r) => r).length / newRevealed.length > 0.9) {
          // If more than 90% revealed, increase chance of revealing remaining
          const remainingUnrevealed = newRevealed
            .map((revealed, index) =>
              revealed || spaceIndices.includes(index) ? -1 : index
            )
            .filter((idx) => idx !== -1);

          // Randomly reveal some of the remaining characters to avoid getting stuck
          remainingUnrevealed.forEach((idx) => {
            if (Math.random() > 0.7) {
              // 30% chance to reveal each remaining character
              newRevealed[idx] = true;
            }
          });
        }

        return newRevealed;
      });

      // Safety check: if animation has been running for too long, force completion
      const allRevealed = revealedIndices.every((r) => r);
      if (!allRevealed) {
        const revealedCount = revealedIndices.filter((r) => r).length;
        const totalCount = revealedIndices.length;

        // If we've revealed most characters but not all (possibly stuck)
        if (revealedCount / totalCount > 0.95) {
          // Force complete the animation after a few more iterations
          setTimeout(() => {
            setRevealedIndices(Array(text.length).fill(true));
            setDisplayText(text);
          }, speed * 5);
        }
      }
    }, speed);

    return () => clearInterval(intervalId);
  }, [text, speed, revealRate, characterPool, revealedIndices, spaceIndices]);

  // Final check to ensure text is correct when animation completes
  useEffect(() => {
    if (revealedIndices.every((revealed) => revealed)) {
      setDisplayText(text);
    }
  }, [revealedIndices, text]);

  return <span className={className}>{displayText}</span>;
};
